/**
 * Rolling "3 days ago and later" campaign window filter (R3, Key Technical
 * Decision 11).
 *
 * Launcher's `GET /api/tools/launcher/campaigns` has no window-filter param
 * (verified — `createListCampaigns()` in `mini-apps/launcher/api.ts` orders
 * by `start_date DESC` and returns everything behind the type-config join).
 * Lead-scanner applies this filter itself to the `event_date` field that
 * route returns (`c.start_date AS event_date`, formatted `"YYYY-MM-DD"` or
 * `null` via `formatDate()` in that same file) — one pure, importable
 * function so the frontend and any future backend caller share the exact
 * same boundary, never two ad-hoc copies of the date math.
 *
 * Boundary (UTC-based, documented per the plan's requirement):
 *   cutoff = UTC-midnight-of(now) − 3 days
 *   isInWindow = eventDate is a valid calendar date AND eventDate >= cutoff
 *
 * An event dated exactly 3 days before `now`'s UTC calendar date, at
 * midnight, is IN — the boundary is inclusive on the "3 days ago" side.
 * Example: now = 2026-08-25 (any time of day, any timezone-of-caller — `now`
 * is always read in UTC) → cutoff = 2026-08-22T00:00:00Z → an event dated
 * "2026-08-22" is IN, "2026-08-21" is OUT.
 *
 * `null` (and any unparseable) `eventDate` is always excluded — the
 * prototype only ever lists dated events (`docs/lead-scanner-prototype.html`
 * mapper regions), so an event with no date has nothing to be "in the
 * window" relative to.
 */

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

export function isInCampaignWindow(eventDate: string | null, now: Date): boolean {
  if (!eventDate) return false;

  // `event_date` from launcher is a bare "YYYY-MM-DD" calendar date (or
  // already-ISO datetime in principle) — parse as UTC midnight either way so
  // a plain date string isn't interpreted in the runtime's local timezone.
  const parsed = eventDate.length === 10 ? `${eventDate}T00:00:00.000Z` : eventDate;
  const eventMs = Date.parse(parsed);
  if (Number.isNaN(eventMs)) return false;

  const nowUtcMidnight = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const cutoff = nowUtcMidnight - THREE_DAYS_MS;

  return eventMs >= cutoff;
}
